import { TAU } from '../core/math';
import { RNG } from '../core/rng';
import { enemies } from '../entities/enemies';
import { SFX } from '../platform/audio';
import { scene } from '../render/scene';
import { fxShockRing } from '../render/weapon-fx';
import { spawnRing } from '../sim/fx';
import { spawnBullet } from '../sim/weapons';
import { safeSpot } from '../world/arena';
import { P } from '../world/player';
import { mines, strikes } from '../world/pools';
import type { Union } from './unions';
/* ============================================================
   融合 · 第二批（2026-08-05）
   ============================================================

   機制與整批的動機寫在 `data/unions.ts`，這裡只有定義。
   這一檔是**一次性發射**的三個（雷區、彈幕、攻城），
   持續場域的三個在 `data/unions-c.ts` —— 分檔的理由是單檔 300 行上限，
   而「有沒有 `update`」正好是這六個最自然的分界。

   兩個融合用了 `w-tether`（牽引錨）。它是池子裡唯一不造成傷害的武裝，
   所以它沒有進化（量測台會擋掉零輸出的來源），
   **融合是它天生的出口**：把敵人搬到某個地方這件事，只有在
   「那個地方有東西在等」的時候才有意義。

   ## 2026-08-05 追加：質變等級（`CRAFT_MAX = 3`）

   三條的等級語言（見 `.claude/notes/craft-levels.md`）：Lv1 不動（下面三條
   逐位元對照改動前），Lv2 動「產能軸」（一律選冷卻——三條的顆數／傷害
   都跟場地判定綁在一起，冷卻是唯一不會牽動判定的旋鈕），Lv3 冷卻再降
   一點＋各自拿掉一條行為限制。三條的 Lv3/Lv1 估算都落在 1.6–1.9×，
   算法寫在各自 `fire()` 的等級註解裡。 */

export var UNIONS_B: Union[] = [
  {
    /* 牽引錨 + 磁暴地雷 → 重力雷區。
       **兩把單獨做不到的**：牽引錨把人搬過來但沒有東西可以打，
       磁暴地雷有東西可以打但要等人自己踩上去。合起來是**陷阱**——
       雷不再等人踩，是你把人拖進去。 */
    id: 'u-gravemine', n: '重力雷區', kind: '融合', axes: ['範圍', '衝擊'],
    union: { a: 'w-tether', b: 'w-mine' },
    turn: 3, turnD: '敵人整批拖進雷區 → 連 Boss 也拖得動（力道減半，才不會秒暈）',
    d: function (lv) {
      return '腳下鋪一圈重力雷，並把周圍的敵人整批拖進雷區（冷卻 '
        + (lv < 2 ? '2.2' : lv < 3 ? '1.7' : '1.3') + ' 秒）'
        + (lv < 3 ? '' : '；**連 Boss 也拖得動**（力道減半）');
    },
    cd: function (lv) { return lv < 2 ? 2.2 : lv < 3 ? 1.7 : 1.3; },
    fire: function (lv) {
      /* 顆數／單顆傷害／雷區半徑全部不動 —— 這把武裝的產能軸是**冷卻**：
         「腳下永遠是一片雷區」的密度本來就取決於多久補一次，不是
         每次補幾顆（顆數再往上加，雷區會擠到互相蓋住觸發半徑）。

         等級（2026-08-05，`CRAFT_MAX`）：
           Lv2  冷卻 2.2 → 1.7（吞吐 +29%）
           Lv3  冷卻 1.7 → 1.3（再 +31%，累計 ×1.69，落在 1.6–1.9× 帶寬）
                ＋ Boss 解禁（見下，不計入吞吐估算——量測台的密集場只有雜兵）。 */
      var ar = P.s.area || 1;
      /* 先佈雷再拉人：順序反過來的話，被拉進來的敵人會先站在空地上，
         而它們的速度在下一步就被場地阻力吃掉一半。 */
      for (var k = 0; k < 5; k++) {
        var mn = mines.get(); if (!mn) break;
        var a = (k / 5) * TAU + P.faceAim;
        var sp = safeSpot(P.pos.x + Math.cos(a) * 5.5, P.pos.z + Math.sin(a) * 5.5, 1);
        mn.m.position.set(sp.x, 0, sp.z); mn.m.scale.setScalar(0.01);
        mn.life = 0; mn.arm = 0.25; mn.dmg = 124; mn.who = 'u-gravemine';   // ⚠️ 配平：176 →（第一版 ×4.55）
        mn.trigR = 6.8 * ar;
        mn.blast = 8.4 * ar;
        mn.ttl = 9 * (P.s.dur || 1);
        scene.add(mn.m);
      }
      var R = 22 * ar, pulled = 0;
      for (var i = 0; i < enemies.length; i++) {
        var e = enemies[i];
        /* Lv3 拿掉的第二條限制：**Boss 拉不動**（跟 `w-tether` 共用的原始
           限制——單獨那把武裝拉 Boss 太強）。但這裡是融合，兩把都要練滿，
           而且「陷阱」這個賣點對 Boss 完全沒用的話等於半殘。

           **力道對 Boss 打到 0.25。** 這個數字不是手感挑的：全遊戲的擊退
           對 Boss 一律乘 0.12（`sim/fx.ts:188`），一把武裝自己開到 0.5
           會跟每一發子彈的表現互相矛盾。0.25 是「比擊退明顯、但不足以
           把 Boss 鎖在腳邊」——每 1.3 秒一次的拖曳配上 Boss 自己的
           `damp` 回正（`sim/boss-ai.ts:244`），讀起來是拽了一下不是綁住。 */
        if (e.T.boss && lv < 3) continue;
        var dx = P.pos.x - e.pos.x, dz = P.pos.z - e.pos.z, d = Math.hypot(dx, dz);
        if (d > R || d < 0.5) continue;
        var kk = 26 * (e.T.boss ? 0.25 : 1) / d;
        e.vel.x += dx * kk; e.vel.z += dz * kk;
        pulled++;
      }
      if (pulled) { spawnRing(P.pos.x, P.pos.z, 0xff8a3d, R, 2, 0.4); SFX.dash(); }
    }
  },
  {
    /* 反射彈 + 後衛散布 → 亂反射風暴。
       **兩把單獨做不到的**：反射彈要你貼著場邊才有價值（角落是它的舞台），
       後衛散布要你猜敵人從哪一側來。合起來兩個條件同時消失 ——
       整個場地被填滿來回穿梭的彈道，**站哪裡都一樣**。 */
    id: 'u-storm', n: '亂反射風暴', kind: '融合', axes: ['傷害', '範圍'],
    union: { a: 'w-ricochet', b: 'w-rear' },
    turn: 3, turnD: '命中兩下就消失 → 貫穿數 1→2，前排打不空這把武裝',
    d: function (lv) {
      return '朝全周射出會反彈的彈丸，滿場來回穿梭，反彈時再分裂（冷卻 '
        + (lv < 2 ? '1.15' : lv < 3 ? '0.92' : '0.85') + ' 秒）'
        + (lv < 3 ? '' : '；**貫穿數提升到 2**，不會被前排兩下打死');
    },
    cd: function (lv) { return lv < 2 ? 1.15 : lv < 3 ? 0.92 : 0.85; },
    fire: function (lv) {
      /* 顆數（8）、反彈次數（6）、分裂（1）全部不動 —— 產能軸是**冷卻**。
         貫穿數留到 Lv3 當「第二條限制」，不是 Lv2 的產能，因為它動的是
         「彈丸活不活得到反彈」這個行為，不是單純的量。

         等級（2026-08-05）：
           Lv2  冷卻 1.15 → 0.92（吞吐 +25%），貫穿不動
           Lv3  冷卻再 → 0.85（累計 +35%）＋ 貫穿 1 → 2（見下）。
         估算：貫穿 1 時一顆彈丸命中兩下就死（260 + 260×0.75 ≈ 455 總傷／顆，
         `sim/projectiles.ts:63` 每次命中扣一次 pierce、傷害×0.75）；
         貫穿 2 撐到四下（260+195+146+110 ≈ 601 總傷／顆，×1.32）。
         單顆傷害倍率 ×1.32 疊上冷卻倍率 ×1.35 ≈ ×1.79，落在 1.6–1.9× 帶寬
         （前提：場上有足夠目標讓貫穿吃滿，密集場成立，稀疏場會偏低）。 */
      var n = 8, crit = RNG.next() < P.s.crit;
      var pierce = lv < 3 ? 1 : 2;
      for (var i = 0; i < n; i++) {
        var a = P.faceAim + (i / n) * TAU;
        var b = spawnBullet(P.pos.x, P.pos.z, a, 260 * (crit ? P.s.critMul : 1), crit, 48, pierce, 0, 0.5, undefined, 'u-storm');
        /* Lv3 拿掉的第二條限制：**貫穿數只有 1，前兩下就把彈丸打死**。
           這把武裝的賣點是「滿場來回穿梭」，但站在敵群裡它常常還沒飛到
           牆就先被前排吃掉——反彈與分裂那部分從沒發生過。貫穿數提到 2
           之後它更常真的活著撐到牆邊，「反彈」才不只是設定表上的數字。
           反彈次數／分裂不動（`bounce`/`split` 不是這把的產能軸，動了
           會讓子彈池被一把武裝吃光，`fire()` 上面的原始註解就是在講這個）。 */
        if (b) { b.bounce = 6; b.split = 1; b.ttl = 5; }
      }
      /* 全周齊射的那一拍。八個質變裡只有這一把完全沒有發射回饋 ——
         八發同時離膛卻沒有任何一下，讀起來像彈丸自己冒出來的。 */
      fxShockRing(P.pos.x, P.pos.z, 0, 1.6, 9.5, 0.28, 0.7);
      SFX.shoot();
    }
  },
  {
    /* 牽引錨 + 軌道光柱 → 重力攻城。
       **兩把單獨做不到的**：光柱最大的問題是**落點是猜的**（0.7 秒預告，
       敵人早就走開了），而牽引錨可以決定敵人在哪裡。
       合起來之後落點不再是預測，是**你搬出來的**——
       先把整群拖到一個點，再把光柱砸在那個點上。 */
    id: 'u-siege', n: '重力攻城', kind: '融合', axes: ['傷害', '衝擊'],
    union: { a: 'w-tether', b: 'w-orbital' },
    turn: 3, turnD: '一次只能砸一群 → 同時集結兩群，各自挨一根光柱',
    d: function (lv) {
      return '把整群敵人拖到一個點，再朝那個點降下一發巨型光柱（冷卻 '
        + (lv < 2 ? '3.4' : lv < 3 ? '2.7' : '2.4') + ' 秒）'
        + (lv < 3 ? '' : '；**同時集結第二群，追加一根較弱的光柱**');
    },
    cd: function (lv) { return lv < 2 ? 3.4 : lv < 3 ? 2.7 : 2.4; },
    fire: function (lv) {
      /* 集結點與光柱都不動 —— 產能軸是**冷卻**。第二根留到 Lv3 當
         「第二條限制」，因為它動的是「能不能同時處理兩群」這個行為，
         不是單純加快同一群的節奏。

         等級（2026-08-05）：
           Lv2  冷卻 3.4 → 2.7（吞吐 +26%）
           Lv3  冷卻再 → 2.4（累計 +42%）＋ 第二群集結點，副砲 70（見下）。
         估算：單次輸出 (271+70)/271 ≈ ×1.26，疊上冷卻倍率 ×1.42
         ≈ ×1.78，落在 1.6–1.9× 帶寬（前提同上：場上有第二群可拖，
         單一小群的場合第二根不發，實際倍率會偏低）。 */
      var ar = P.s.area || 1;
      var c1 = siegeRally(ar, null);
      var p1 = c1
        ? { x: c1.pos.x, z: c1.pos.z }
        : { x: P.pos.x + Math.sin(P.faceAim) * 12, z: P.pos.z + Math.cos(P.faceAim) * 12 };
      /* ⚠️ 2026-08-05 收斂：只動冷卻 ＋ 副砲 70，量測台量到成長 ×1.29
         （帶寬 1.6–1.9×）。真因是**光柱池只有 10 個**（`world/pools.ts`，
         四把武裝共用），縮冷卻擠不進更多根；而第二群在密集場多半不存在
         （敵人都被拖成一群），所以副砲那一根常常不發。
         成長改走「主砲單發」這條不吃池子的軸。 */
      siegeBeam(p1.x, p1.z, ar, lv < 2 ? 271 : (lv < 3 ? 320 : 380));
      /* Lv3 拿掉的第二條限制：**一次只能砸一群**。分散成兩群的敵人
         原本只有比較密的那一群倒楣，另一群完全沒事。Lv3 之後排除第一群
         附近（14 單位內）再找一次集結點，兩群各自被拖、各自挨一根——
         找不到第二群（只有一群敵人）就不發，不是硬湊一根打空氣。 */
      if (lv >= 3) {
        var c2 = siegeRally(ar, p1);
        if (c2) siegeBeam(c2.pos.x, c2.pos.z, ar, 70);
      }
    }
  }
];

/** 找「敵人最多的那一區」的中心（周圍 10 單位內同伴最多的那一隻），
    只在玩家周圍 30 單位內找（拖不到的距離挑了也沒用）。
    `exclude` 給 Lv3 找第二群用——排除第一群落點 14 單位內的候選，
    不排除的話第二群幾乎一定挑到跟第一群同一撮。
    抽成函式是因為 Lv3 要呼叫兩次，複製一份的話兩群的判定會各自漂移。 */
function siegeRally(ar: number, exclude: { x: number, z: number } | null) {
  var best = null, bestC = -1;
  for (var i = 0; i < enemies.length; i++) {
    var e = enemies[i];
    if (exclude && Math.hypot(e.pos.x - exclude.x, e.pos.z - exclude.z) < 14) continue;
    if (Math.hypot(e.pos.x - P.pos.x, e.pos.z - P.pos.z) > 30) continue;
    var c = 0;
    for (var j = 0; j < enemies.length; j++) {
      if (Math.hypot(enemies[j].pos.x - e.pos.x, enemies[j].pos.z - e.pos.z) < 10) c++;
    }
    if (c > bestC) { bestC = c; best = e; }
  }
  return best;
}

/** 把某個點附近的敵人拖過去，再朝那個點降下一根光柱。
    `dmg` 分開傳是為了 Lv3 的副砲比主砲弱（見 `fire()` 的等級註解）。 */
function siegeBeam(tx: number, tz: number, ar: number, dmg: number) {
  for (var k = 0; k < enemies.length; k++) {
    var e2 = enemies[k];
    if (e2.T.boss) continue;
    var dx = tx - e2.pos.x, dz = tz - e2.pos.z, d = Math.hypot(dx, dz);
    if (d > 24 * ar || d < 0.5) continue;
    var kk = 30 / d;
    e2.vel.x += dx * kk; e2.vel.z += dz * kk;
  }
  var st = strikes.get(); if (!st) return;
  st.m.position.set(tx, 0, tz);
  /* 預告 0.75 秒：比來源的 0.7 長一點點。**拉扯需要時間生效**——
     預告太短的話光柱會落在敵人被拖走的路上，而不是集結完的那一堆上。 */
  st.radius = 13 * ar; st.dmg = dmg; st.who = 'u-siege';
  st.life = 0; st.delay = 0.75; st.fired = false;
  st.ring.scale.setScalar(st.radius); st.ring.material.opacity = 0.85;
  st.col.scale.set(st.radius * 1.2, 60, st.radius * 1.2); st.col.material.opacity = 0;
  scene.add(st.m);
  spawnRing(tx, tz, 0xff8a3d, 24 * ar, 3, 0.45);
  SFX.beam();
}
